
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useSharePoint } from '@/context/SharePointContext';
import { toast } from '../ui/use-toast';
import { Link } from 'lucide-react';
import { initializeSP } from '@/services/pnpjsConfig';
import { msalInstance, tokenRequest } from '@/msalAuth';
import * as msalAuth from '@/msalAuth'; // Import all exports from msalAuth

const ConnectionModeToggle = () => {
  const { 
    siteUrl,
    setSiteUrl,
  } = useSharePoint();
  
  const [localSiteUrl, setLocalSiteUrl] = useState<string>('');
  const [isConfiguring, setIsConfiguring] = useState(false);

  // Load the site URL when component mounts
  useEffect(() => {
    if (siteUrl) {
      setLocalSiteUrl(siteUrl);
    } else {
      // Default value if no site URL is set
      setLocalSiteUrl('https://netorgft10863452.sharepoint.com');
    }
  }, [siteUrl]);

  const handleSiteUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSiteUrl(e.target.value);
  };

  const handleSiteUrlSave = () => {
    setSiteUrl(localSiteUrl);
    // Update the SP instance with new site URL
    initializeSP(localSiteUrl);
    toast({
      title: "Site URL Updated",
      description: `SharePoint site URL has been set to: ${localSiteUrl}`,
    });
  };

  const handleMsalConfig = async () => {
    setIsConfiguring(true);
    try {
      // Update site URL in context and PnPjs
      setSiteUrl(localSiteUrl);
      initializeSP(localSiteUrl);
      
      // Ensure we have an active account, or login if needed
      // This is ONLY for SharePoint connection, not the main app auth
      msalAuth.setActiveAccountIfNeeded();
      const activeAccount = msalInstance.getActiveAccount();
      
      if (!activeAccount) {
        // No account, perform interactive login
        const loginResponse = await msalInstance.loginPopup(tokenRequest);
        msalInstance.setActiveAccount(loginResponse.account);
      } else {
        // Try to get a token, fallback to interactive if needed
        try {
          await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account: activeAccount
          });
        } catch (error) {
          console.warn("Silent token acquisition failed, fallback to interactive", error);
          const response = await msalInstance.acquireTokenPopup({
            ...tokenRequest,
            account: activeAccount
          });
          
          // Ensure we update the active account
          if (response.account) {
            msalInstance.setActiveAccount(response.account);
          }
        }
      }
      
      toast({
        title: "Site URL configured",
        description: "SharePoint site URL has been configured for MSAL authentication",
      });
    } catch (error) {
      console.error('Error configuring MSAL:', error);
      toast({
        title: "Configuration error",
        description: "An error occurred while configuring MSAL authentication",
        variant: "destructive"
      });
    } finally {
      setIsConfiguring(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>SharePoint Connection</CardTitle>
        <CardDescription>
          Configure connection to SharePoint using PnPjs with MSAL authentication
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <Label htmlFor="site-url">SharePoint Site URL</Label>
            <Input 
              id="site-url" 
              placeholder="https://contoso.sharepoint.com/sites/mysite" 
              value={localSiteUrl}
              onChange={handleSiteUrlChange}
              className="mt-1"
            />
            <Button 
              onClick={handleSiteUrlSave} 
              variant="outline" 
              className="mt-2"
              size="sm"
            >
              Save URL
            </Button>
          </div>
          
          <Button 
            onClick={handleMsalConfig} 
            variant="default" 
            disabled={isConfiguring || !localSiteUrl}
            className="mt-2 w-full"
          >
            <Link className="mr-2 h-4 w-4" />
            {isConfiguring ? 'Configuring...' : 'Configure SharePoint with MSAL'}
          </Button>
          
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md text-sm">
            <p className="font-medium">MSAL Authentication for SharePoint:</p>
            <p>This authentication method uses Microsoft Authentication Library to securely access SharePoint only.</p>
            <p className="mt-2">Users will need to sign in with their Microsoft 365 credentials specifically for SharePoint access.</p>
            <p className="mt-2 text-xs text-amber-600">Note: This is separate from your main app authentication.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConnectionModeToggle;
